import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules"; // Autoplay optional
import { useParams } from "react-router-dom";
import Header from "../../../components/header/Header";
import Sidebar from "../../../components/sidebar/Sidebar";
import api from "../../../utils/api";
import SpinnerOverlay from "../../../components/spinnerOverlay/SpinnerOverlay";
import { API_BASE_URL } from "../../../utils/apiUrl";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import { toast } from "react-hot-toast";
import "./TutorProfilePage.css";
import ProfileSkeleton from "../../../components/profileSkeleton/ProfileSkeleton";
import { getImageSrc } from "../../../utils/getImageSrc";
import { useAuth } from "../../../context/AuthContext";
import "./videosGrid.css";
import { updateUserProfile } from "../../../utils/userApi";
import { formatRate } from "../../../utils/formatters";
import BioDisplay from "../../../components/bioDisplay/BioDisplay";
import BadgeList from "../../../components/badgeList/BadgeList";
import { getEarnedBadges } from "../../../utils/badges";
import ShareProfileButtons from "../../../components/shareProfile/ShareProfileButtons";
import { Helmet } from "react-helmet";

const TutorProfilePage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [introMessage, setIntroMessage] = useState("");
  const [introSubmitting, setIntroSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [ratings, setRatings] = useState({
    lessonQuality: 0,
    performance: 0,
    communication: 0,
    valueForMoney: 0,
  });
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);

  // Add inside the component, near your other useState hooks
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = React.useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top smoothly when the component mounts / route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth" if you prefer a gentle scroll
    });
  }, []);

  // console.log("provider", provider);

  // Track Profile Visit when page loads
  useEffect(() => {
    if (!provider?._id) return;

    const trackVisit = async () => {
      try {
        await api.post(`/analytics/track`, {
          entityId: provider._id,
          entityType: provider.role, // "tutor" or "institution"
          action: "visit",
        });
        console.log("✅ Profile visit tracked");
      } catch (err) {
        console.error("Failed to track visit:", err);
      }
    };

    trackVisit();
  }, [provider?._id, provider?.role]);

  useEffect(() => {
    if (!provider?._id) return;

    const fetchReviews = async () => {
      try {
        // console.log("Fetching reviews for ID:", provider._id); // ← debug

        const res = await api.get(`/reviews?reviewed=${provider._id}`);

        console.log("Reviews API response:", res.data); // ← very important log

        setReviews(res.data.reviews || []);
        setAverageRating(res.data.averageRating || provider.averageRating || 0);
        setReviewCount(res.data.reviewCount || provider.reviewCount || 0);
      } catch (err) {
        console.error(
          "Reviews fetch failed:",
          err.response?.data || err.message,
        );
        // setProvider(null);
        setAverageRating(provider.averageRating || 0);
        setReviewCount(provider.reviewCount || 0);
      }
    };

    fetchReviews();
  }, [provider?._id]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get(`/users/public-profile/${id}`);

        setProvider(res.data);
        console.log("Profile API response:", res.data);

        // Resolved via an old slug or raw _id (see User.previousSlugs /
        // scripts/migrate-to-personalized-slugs.js) — settle the address
        // bar on the current canonical URL. `replace` so the old link
        // doesn't land in browser history as a separate entry.
        if (res.data.isLegacyUrl && res.data.slug && res.data.slug !== id) {
          const base = res.data.role === "institution" ? "academy" : "tutor";
          navigate(`/${base}/${res.data.slug}`, { replace: true });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id, navigate]);

  // ==================== CONTACT VIA TUTORSLINKS ====================
  // Providers' personal phone numbers are never shown publicly — contact
  // always routes through TutorsLinks' own channel so the platform can
  // mediate the introduction.
  const handleContactClick = () => {
    if (!provider?._id) return;

    api
      .post(`/analytics/track`, {
        entityId: provider._id,
        entityType: provider.role,
        action: "contact_click",
      })
      .catch((err) => console.error("Failed to track contact click:", err));

    navigate(`/get-help?provider=${provider._id}&name=${encodeURIComponent(provider.displayName || "")}`);
  };

  // Training centre "Request Information / Enrolment Enquiry" — same
  // contact-routing flow as handleContactClick above (no phone numbers are
  // ever exposed publicly), just a different prefilled intent.
  const handleEnquiryClick = () => {
    if (!provider?._id) return;

    api
      .post(`/analytics/track`, {
        entityId: provider._id,
        entityType: provider.role,
        action: "enquiry_click",
      })
      .catch((err) => console.error("Failed to track enquiry click:", err));

    navigate(
      `/get-help?provider=${provider._id}&name=${encodeURIComponent(provider.displayName || "")}&intent=enrolment`,
    );
  };

  // ==================== REQUEST INTRO SESSION ====================
  const handleRequestIntroClick = () => {
    if (!user) {
      navigate("/login", {
        state: {
          message: "Log in to request an intro session.",
          redirectTo: window.location.pathname,
        },
      });
      return;
    }
    setShowIntroModal(true);
  };

  const handleIntroSubmit = async () => {
    setIntroSubmitting(true);
    try {
      await api.post("/lesson-sessions", {
        tutorId: provider._id,
        type: "intro",
        message: introMessage.trim(),
      });
      toast.success("Request sent! The provider will respond soon.");
      setShowIntroModal(false);
      setIntroMessage("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send request");
    } finally {
      setIntroSubmitting(false);
    }
  };

  // ==================== REPORT PROFILE ====================
  const handleReportClick = () => {
    if (!user) {
      navigate("/login", {
        state: {
          message: "Log in to report a profile.",
          redirectTo: window.location.pathname,
        },
      });
      return;
    }
    setShowReportModal(true);
  };

  const handleReportSubmit = async () => {
    if (!reportReason.trim()) return;
    setReportSubmitting(true);
    try {
      await api.post("/chat/report", {
        profileId: provider._id,
        profileName: provider.displayName,
        reason: reportReason.trim(),
      });
      toast.success("Report submitted. Our team will review it shortly.");
      setShowReportModal(false);
      setReportReason("");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to submit report");
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCoverUploading(true);
    try {
      const updated = await updateUserProfile({ coverImage: file });
      // Merge the new coverImage into local provider state so it renders immediately
      setProvider((prev) => ({ ...prev, coverImage: updated.coverImage }));
      toast.success("Cover image updated!");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to update cover image",
      );
    } finally {
      setCoverUploading(false);
      e.target.value = ""; // reset input so same file can be re-selected
    }
  };

  const canSubmit =
    comment.trim().length > 0 && Object.values(ratings).some((r) => r > 0);

  const resetReviewForm = () => {
    setComment("");
    setRatings({
      lessonQuality: 0,
      performance: 0,
      communication: 0,
      valueForMoney: 0,
    });
  };

  const handleSubmitReview = async () => {
    // but just in case someone calls it programmatically
    if (!user) {
      navigate("/login", {
        state: {
          message: "Please log in to leave a review.",
          redirectTo: window.location.pathname,
        },
      });
      return;
    }

    try {
      const overallRating =
        (ratings.lessonQuality +
          ratings.performance +
          ratings.communication +
          ratings.valueForMoney) /
        4;

      await api.post("/reviews", {
        reviewedId: provider._id,
        overallRating: Math.round(overallRating * 10) / 10,
        lessonQuality: ratings.lessonQuality,
        performance: ratings.performance,
        communication: ratings.communication,
        valueForMoney: ratings.valueForMoney,
        title: reviewTitle || "Great Experience",
        comment,
      });

      // Reset form state
      setReviewTitle("");
      resetReviewForm();

      // Refresh reviews and ratings in the background
      const res = await api.get(`/reviews?reviewed=${provider._id}`);
      setReviews(res.data.reviews || []);
      setAverageRating(res.data.averageRating || 0);
      setReviewCount(res.data.reviewCount || 0);

      // Show success modal LAST — onClose is called when user dismisses it
      setShowSuccessModal(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit review");
    }
  };

  const StarRating = ({ value, onChange }) => {
    return (
      <div className="stars-row">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${value >= star ? "filled" : ""}`}
            onClick={() => onChange(star)}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  // console.log("provider", provider);

  if (loading) return <ProfileSkeleton />;

  if (!provider) {
    return (
      <div className="loading-screen profile-not-found">
        <h2>Profile not found</h2>
        <p>Sorry, we couldn't load this profile.</p>
        <button className="back-button" onClick={() => window.history.back()}>
          Go Back
        </button>
      </div>
    );
  }

  // console.log("provider", provider);

  const isTutor = provider?.role === "tutor";
  const isInstitution = provider?.role === "institution";
  const earnedBadges = getEarnedBadges(provider);

  // Is the currently logged-in user viewing their OWN profile?
  // (false for guests, and false when a logged-in user views someone else's profile)
  const isOwnProfile = !!user && user._id === provider._id;

  // === TEACHING MODES (New + Legacy Support) ===
  const teachingModes = provider?.teachingModes || [];

  // Map mode values to nice display labels. Harmonized to 3 modes
  // (online/hybrid/physical) — old keys kept as a fallback for any record
  // not yet migrated, see scripts/migrateTeachingModes.js.
  const modeLabels = {
    online: "Online",
    hybrid: "Hybrid",
    physical: "Physical",
    "online-live": "Online",
    "online-self-paced": "Online",
    "in-person-private": "Physical",
    "in-person-classroom": "Physical",
    others: "Physical",
  };

  const displayModes =
    teachingModes.length > 0
      ? teachingModes.map((mode) => modeLabels[mode] || mode.replace(/-/g, " "))
      : [];

  // Legacy fallback
  const legacyProgramType = provider?.programType
    ? provider.programType === "physical"
      ? "In-Person"
      : provider.programType === "online"
        ? "Online"
        : provider.programType === "hybrid"
          ? "Hybrid"
          : provider.programType
    : null;

  // === NEW STRUCTURE SUPPORT ===
  const selectedCategories = provider?.selectedCategories || [];
  const categoryDetails = provider?.categoryDetails || {};
  // const teachingModes = provider?.teachingModes || [];

  // Get all courses across categories
  const allCourses = selectedCategories.flatMap(
    (group) => categoryDetails[group]?.courses || [],
  );

  // Compact subject chips shown above the fold — prefer the backend's
  // already-deduped/flattened list (includes customCourses) when present.
  const headerSubjects = [
    ...new Set((provider?.allCourses?.length ? provider.allCourses : allCourses).filter(Boolean)),
  ];

  // Rate display (from first category)
  const rateDisplay = (() => {
    if (selectedCategories.length === 0) return "Rate on request";
    const firstGroup = selectedCategories[0];
    const details = categoryDetails[firstGroup];
    return formatRate(details?.rate, details?.period || "hour");
  })();

  const displayName = provider.displayName;

  const programLabel =
    provider.programType === "online"
      ? "Online Class"
      : provider.programType === "physical"
        ? "Physical Class"
        : "Online & Physical";

  // const avatarSrc = provider.picture?.url
  //   ? `${API_BASE_URL}${provider.picture.url}`
  //   : provider.picture // fallback if picture is still a string (old data)
  //     ? `${API_BASE_URL}${provider.picture}`
  //     : "/images/avatar3.png";

  const avatarSrc = getImageSrc(provider?.picture);

  const locationShort = provider.generalLocation || "Location not specified";

  const rate = (() => {
    if (provider?.pricingSummary?.lowestRate) {
      return formatRate(
        provider.pricingSummary.lowestRate,
        provider.pricingSummary.lowestPeriod,
      );
    }
    if (selectedCategories.length > 0) {
      const firstCat = categoryDetails[selectedCategories[0]];
      return formatRate(firstCat?.rate, firstCat?.period || "hour");
    }
    return formatRate(provider?.hourlyRate, provider?.ratePeriod || "hour");
  })();

  const programTypes = [];
  if (provider.programType?.includes("online"))
    programTypes.push("Online Training");
  if (provider.programType?.includes("physical"))
    programTypes.push("Physical Training");

  // Prepare education data – currently only one entry possible
  const educationEntries = [];

  if (provider.schoolName && provider.fieldOfStudy) {
    let years = "";
    educationEntries.push({
      schoolName: provider.schoolName,
      degree: provider.degree || "Bachelor's",
      fieldOfStudy: provider.fieldOfStudy,
      years: years || "Period not specified",
    });
  }

  const hasEducation = educationEntries.length > 0;

  // FIXED: Safe handling for mainProgram (string) and selectedCourses (array)
  const mainProgramArray = provider.mainProgram
    ? [provider.mainProgram] // convert string to array for mapping
    : [];

  const selectedCoursesArray = Array.isArray(provider.selectedCourses)
    ? provider.selectedCourses
    : [];

  function PublicVideoCard({ vid, baseUrl }) {
    const videoRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);

    const toggle = async () => {
      const video = videoRef.current;
      if (!video) return;
      if (playing) {
        video.pause();
        setPlaying(false);
      } else {
        try {
          await video.play();
          setPlaying(true);
        } catch (err) {
          // AbortError is harmless — browser interrupted play() before it resolved
          if (err.name !== "AbortError")
            console.error("Video play error:", err);
          setPlaying(false);
        }
      }
    };

    const timeAgo = (dateStr) => {
      const diff = Date.now() - new Date(dateStr).getTime();
      const days = Math.floor(diff / 86400000);
      if (days === 0) return "Today";
      if (days === 1) return "Yesterday";
      if (days < 30) return `${days} days ago`;
      const months = Math.floor(days / 30);
      return `${months} month${months > 1 ? "s" : ""} ago`;
    };

    return (
      <div className="pub-video-card">
        {/* Thumbnail / player */}
        <div className="pub-video-thumb-wrap" onClick={toggle}>
          <video
            ref={videoRef}
            src={`${baseUrl}${vid.url}`}
            className="pub-video-thumb"
            preload="metadata"
            onEnded={() => setPlaying(false)}
          />
          {!playing && (
            <div className="pub-play-overlay">
              <div className="pub-play-btn">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="#1e293b">
                  <path d="M4 2.5l9 5.5-9 5.5V2.5z" />
                </svg>
              </div>
            </div>
          )}
        </div>

        {/* Info row */}
        <div className="pub-video-info">
          <div className="pub-video-title-row">
            <p className="pub-video-title">{vid.title}</p>
            <button className="pub-video-more" aria-label="More options">
              ⋯
            </button>
          </div>
          <p className="pub-video-date">Uploaded {timeAgo(vid.uploadedAt)}</p>
          <p className="pub-video-views">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            {vid.views ?? 0} Viewed
          </p>
        </div>
      </div>
    );
  }

  const profileMetaDescription = (
    provider.bioIntro ||
    provider.bio ||
    provider.about ||
    `${displayName} on TutorsLinks — ${isTutor ? "tutor" : "training centre"} offering ${selectedCategories.join(", ") || "lessons"}.`
  ).slice(0, 160);

  // Permanent, SEO-friendly canonical URL — pinned to provider.slug (the
  // canonical field from the API, same pattern as SingleBlogPage.jsx), not
  // the raw route param, since this page is also reachable via the older
  // raw-_id form for backwards compatibility. Tells Google there's exactly
  // one indexable identity per profile even while both URLs resolve.
  const canonicalSlugOrId = provider.slug || provider._id;
  const canonicalPath = `${isInstitution ? "/academy" : "/tutor"}/${canonicalSlugOrId}`;

  return (
    <div className="tutors-app">
      <Helmet>
        <title>{`${displayName} — ${isTutor ? "Tutor" : "Training Centre"} | TutorsLinks`}</title>
        <meta name="description" content={profileMetaDescription} />
        <link
          rel="canonical"
          href={`https://www.tutorslinks.com${canonicalPath}`}
        />
      </Helmet>

      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <main className={`student-main ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="profile-wrapper">
          {/*
            ===== COVER UNDERLAY =====
            The cover image is now a full background layer that spans from the very
            top (behind the "Welcome" greeting and page title) all the way down
            through the avatar and the city/country line beneath it.
            Everything inside `.profile-cover-underlay` sits ON TOP of the image
            (see CSS notes below the component for the required styles).
          */}
          <div
            className="profile-cover-underlay"
            style={
              provider.coverImage?.url
                ? {
                    backgroundImage: `url(${provider.coverImage.url})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : undefined
            }
          >
            {/* Dark gradient so text/controls stay readable over any photo */}
            <div className="profile-cover-scrim" />

            <button
              className="back-btn"
              onClick={() => window.history.back()}
              aria-label="Go back"
            >
              ←
            </button>

            {/* Only the authenticated user viewing their OWN profile sees the greeting */}
            {isOwnProfile && (
              <div className="greeting-wrapper">
                <h1 className="greeting-title">Welcome, {displayName}</h1>
                <h2 className="greeting-subtitle">
                  Explore available tutors and training centers
                </h2>
              </div>
            )}

            <h1 className="page-title">
              {isTutor ? "Tutor Profile" : "Training Center Profile"}
            </h1>

            {isOwnProfile && (
              <>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleCoverChange}
                />
                <button
                  className="cover-edit-btn"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={coverUploading}
                  title="Change cover photo"
                >
                  {coverUploading ? (
                    <span className="cover-upload-spinner" />
                  ) : (
                    <>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8
                a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
                        />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                      Edit Cover
                    </>
                  )}
                </button>
              </>
            )}

            {/* Avatar + display name/rating + city/country now live inside the cover underlay, left-aligned */}
            <div className="cover-avatar-block">
              <img src={avatarSrc} alt="profile" className="profile-avatar" />

              <div className="name-rating-row">
                <h2 className="profile-name">{displayName}</h2>
                <span className="rating-text">
                  {reviewCount > 0
                    ? `⭐ ${averageRating?.toFixed(1) || 0}/5 rating`
                    : "New on TutorsLinks"}
                </span>
              </div>

              <div className="cover-location-text">
                {provider.state || "Abuja"}, {provider.country || "Nigeria"}
              </div>

              {provider.headline && (
                <p className="profile-headline">{provider.headline}</p>
              )}
            </div>
          </div>

          <div className="profile-header">
            <div className="profile-left-vertical">
              <div className="profile-details-vertical">
                {provider.languages && (
                  <div className="language-text">
                    💬 Speaks: {provider.languages.join(", ")}
                  </div>
                )}

                {headerSubjects.length > 0 && (
                  <div className="header-subjects-row">
                    {headerSubjects.slice(0, 6).map((subject) => (
                      <span key={subject} className="header-subject-chip">
                        {subject}
                      </span>
                    ))}
                    {headerSubjects.length > 6 && (
                      <span className="header-subject-chip more">
                        +{headerSubjects.length - 6} more
                      </span>
                    )}
                  </div>
                )}

                {provider.availability && (
                  <div className="availability-text">
                    🗓️ Availability: {provider.availability}
                  </div>
                )}

                <div className="badge-row">
                  {earnedBadges.length > 0 ? (
                    <BadgeList entity={provider} />
                  ) : (
                    <span className="verified-pill">
                      {provider.userApprovalStatus
                        ? provider.userApprovalStatus.charAt(0).toUpperCase() +
                          provider.userApprovalStatus.slice(1)
                        : "Pending"}
                    </span>
                  )}

                  <span className="price-pill">
                    {/* {provider.currency} */}
                    {/* {price}/{ratePeriod} */}
                    {rate}
                  </span>

                  <span className="review-pill">
                    {reviewCount > 0
                      ? `${reviewCount} ${reviewCount === 1 ? "Review" : "Reviews"}`
                      : "No reviews yet"}
                  </span>

                  <span className="lessons-pill">
                    {provider.completedLessonsCount || 0} Completed
                    TutorsLinks Lesson
                    {provider.completedLessonsCount === 1 ? "" : "s"}
                  </span>
                </div>

                {provider.offersIntroSession && (
                  <div className="intro-session-banner">
                    <span>🎁 Offers a free Intro/First Session</span>
                    <button
                      type="button"
                      className="intro-session-btn"
                      onClick={handleRequestIntroClick}
                    >
                      Request Intro Session
                    </button>
                  </div>
                )}

                <div className="info-block address-block">
                  <div className="address-header">
                    <p className="info-label">Service Location</p>
                  </div>

                  <p className="address-text">{provider.generalLocation}</p>
                </div>
              </div>
            </div>
            <div className="profile-right-clean">
              {/* <div className="program-type-row">
                {provider.programType?.includes("online") && (
                  <span className="program-pill outline">🏫 Online Class</span>
                )}

                {provider.programType?.includes("physical") && (
                  <span className="program-pill filled">👨‍🏫 Physical Class</span>
                )}
                {provider.programType?.includes("hybrid") && (
                  <span className="pill outline outline">
                    👨‍🏫 Both Physical and Online Classes
                  </span>
                )}
              </div> */}
              <div className="program-type-row">
                {displayModes.length > 0 ? (
                  displayModes.map((mode, index) => (
                    <span key={index} className="program-pill outline">
                      {mode}
                    </span>
                  ))
                ) : legacyProgramType ? (
                  <span className="program-pill outline">
                    {legacyProgramType}
                  </span>
                ) : (
                  <span className="program-pill outline">
                    Teaching Format Not Specified
                  </span>
                )}
              </div>

              <div className="contact-row">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleContactClick();
                  }}
                  className="whatsapp-clean-btn"
                >
                  <span className="wa-icon">💬</span>
                  Contact via TutorsLinks
                </a>

                {isInstitution && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEnquiryClick();
                    }}
                    className="whatsapp-clean-btn enquiry-btn"
                  >
                    <span className="wa-icon">📩</span>
                    Request Information / Enrolment Enquiry
                  </a>
                )}

                {provider.socialMedia?.link && (
                  <a
                    href={provider.socialMedia.link}
                    target="_blank"
                    rel="noreferrer"
                    className="circle-icon-btn"
                  >
                    {provider.socialMedia.platform === "facebook" && "📘"}
                    {provider.socialMedia.platform === "instagram" && "📸"}
                    {provider.socialMedia.platform === "x" && "✖"}
                    {provider.socialMedia.platform === "linkedin" && "💼"}
                    {provider.socialMedia.platform === "youtube" && "🎬"}
                    {provider.socialMedia.platform === "other" && "🌐"}
                  </a>
                )}

                <ShareProfileButtons
                  url={window.location.href}
                  name={displayName}
                />

                <button
                  type="button"
                  className="report-profile-btn"
                  onClick={handleReportClick}
                >
                  🚩 Report Profile
                </button>
              </div>

              <p className="safety-guidance-note">
                🛡️ Stay safe: keep communication and payments on TutorsLinks,
                never send money before a lesson, and meet in a public or
                well-supervised place for the first in-person session.
              </p>
            </div>
          </div>

          {/* TABS */}
          <div className="profile-tabs">
            {["overview", "testimonials", "career", "map", "media"].map(
              (tab) => (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "career"
                    ? "Credentials"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ),
            )}
          </div>

          <div className="profile-content">
            {activeTab === "overview" && (
              <div className="overview-layout">
                <div className="overview-left">
                  <h3>Bio</h3>
                  <BioDisplay provider={provider} enableReadMore />

                  {provider.videos?.length > 0 && (
                    <div className="overview-intro-video">
                      <h3>Introduction Video</h3>
                      <PublicVideoCard
                        vid={provider.videos[0]}
                        baseUrl={API_BASE_URL}
                      />
                    </div>
                  )}
                </div>

                <div className="overview-right-clean">
                  {/* Program Information */}
                  <div className="info-block">
                    <h3 className="section-title">Program Information</h3>

                    {/* Teaching Modes */}
                    <div className="info-row">
                      <p className="info-label">Teaching Modes</p>
                      <div className="pill-row">
                        {displayModes.length > 0 ? (
                          displayModes.map((mode, i) => (
                            <span key={i} className="pill">
                              {mode}
                            </span>
                          ))
                        ) : legacyProgramType ? (
                          <span className="pill">{legacyProgramType}</span>
                        ) : (
                          <span className="no-data">Not specified</span>
                        )}
                      </div>
                    </div>

                    {/* Categories & Pricing */}
                    {selectedCategories.length > 0 ? (
                      selectedCategories.map((groupName) => {
                        const details = categoryDetails[groupName] || {};
                        return (
                          <div
                            key={groupName}
                            className="info-row"
                            style={{ marginBottom: "24px" }}
                          >
                            <p className="info-label strong">{groupName}</p>

                            <div
                              className="pill-row"
                              style={{ marginBottom: "8px" }}
                            >
                              {details.courses?.length > 0 ? (
                                details.courses.map((course, i) => (
                                  <span key={i} className="pill multi">
                                    {course}
                                  </span>
                                ))
                              ) : (
                                <span className="no-data">
                                  No courses selected
                                </span>
                              )}
                            </div>

                            {details.rate && (
                              <p className="info-label">
                                Rate:{" "}
                                <strong>
                                  {formatRate(details.rate, details.period || "hour")}
                                </strong>
                              </p>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p className="no-data">
                        No courses or categories added yet.
                      </p>
                    )}
                  </div>

                  {/* Training Centre Details — institution profiles only */}
                  {isInstitution && (
                    <div className="info-block institution-details-block">
                      <h3 className="section-title">Training Centre Details</h3>

                      {provider.facilities?.length > 0 && (
                        <div className="info-row">
                          <p className="info-label">Facilities</p>
                          <div className="pill-row">
                            {provider.facilities.map((f, i) => (
                              <span key={i} className="pill multi">
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {provider.ageGroups?.length > 0 && (
                        <div className="info-row">
                          <p className="info-label">Learner Age Groups</p>
                          <div className="pill-row">
                            {provider.ageGroups.map((a, i) => (
                              <span key={i} className="pill">
                                {a}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {provider.accreditations?.length > 0 && (
                        <div className="info-row">
                          <p className="info-label">Accreditations / Affiliations</p>
                          <div className="pill-row">
                            {provider.accreditations.map((a, i) => (
                              <span key={i} className="pill multi">
                                {a}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {provider.openingHours?.some(
                        (d) => !d.closed && (d.open || d.close),
                      ) && (
                        <div className="info-row">
                          <p className="info-label">Opening Hours</p>
                          <ul className="institution-list">
                            {provider.openingHours
                              .filter((d) => !d.closed && (d.open || d.close))
                              .map((d) => (
                                <li key={d.day}>
                                  {d.day}: {d.open || "?"} - {d.close || "?"}
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}

                      {provider.classSchedules?.length > 0 && (
                        <div className="info-row">
                          <p className="info-label">Class Schedules</p>
                          <ul className="institution-list">
                            {provider.classSchedules.map((s, i) => (
                              <li key={i}>
                                <strong>{s.title}</strong>
                                {s.days && ` — ${s.days}`}
                                {s.time && ` — ${s.time}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {provider.branches?.length > 0 && (
                        <div className="info-row">
                          <p className="info-label">Branches</p>
                          <ul className="institution-list">
                            {provider.branches.map((b, i) => (
                              <li key={i}>
                                <strong>{b.name}</strong>
                                {b.address && ` — ${b.address}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {provider.upcomingProgrammes?.length > 0 && (
                        <div className="info-row">
                          <p className="info-label">Upcoming Programmes</p>
                          <ul className="institution-list">
                            {provider.upcomingProgrammes.map((p, i) => (
                              <li key={i}>
                                <strong>{p.title}</strong>
                                {p.startDate &&
                                  ` — starts ${new Date(p.startDate).toLocaleDateString()}`}
                                {p.description && ` — ${p.description}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {provider.photos?.length > 0 && (
                        <div className="info-row">
                          <p className="info-label">Photos</p>
                          <div className="institution-photos-grid">
                            {provider.photos.map((photo, i) => (
                              <img
                                key={photo._id || i}
                                src={photo.url}
                                alt={photo.name || `${displayName} photo ${i + 1}`}
                                loading="lazy"
                                className="institution-photo-thumb"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "testimonials" && (
              <div className="tab-section testimonials-section">
                <h3 className="section-title">Reviews</h3>

                <div className="reviews-swiper-container">
                  <Swiper
                    modules={[Navigation]}
                    spaceBetween={16}
                    slidesPerView={1.1}
                    centeredSlides={false}
                    getReviewsForEntity
                    navigation={{
                      prevEl: ".swiper-button-prev-custom",
                      nextEl: ".swiper-button-next-custom",
                    }}
                    breakpoints={{
                      640: { slidesPerView: 1.8, spaceBetween: 20 },
                      768: { slidesPerView: 2.2, spaceBetween: 24 },
                      1024: { slidesPerView: 3, spaceBetween: 28 },
                    }}
                    loop={reviews.length > 3}
                    grabCursor={true}
                  >
                    {reviews.map((review) => {
                      // Safely get reviewer image
                      const reviewerPicture = review.reviewer?.picture;
                      const reviewerImageSrc = getImageSrc(reviewerPicture);

                      return (
                        <SwiperSlide key={review._id}>
                          <div className="review-card-compact">
                            <div className="review-header-compact">
                              <img
                                src={reviewerImageSrc}
                                alt={`${review.reviewer?.firstName} ${review.reviewer?.lastName}`}
                                className="review-avatar-compact"
                                onError={(e) => {
                                  e.target.src = "/images/avatar2.png"; // fallback on error
                                }}
                              />
                              <div className="review-author-info">
                                <h4 className="review-name-compact">
                                  {review.reviewer?.firstName}{" "}
                                  {review.reviewer?.lastName}
                                </h4>
                              </div>
                            </div>

                            <h5 className="review-title-compact">
                              {review.title || "Review"}
                            </h5>

                            <p className="review-text-compact">
                              {review.comment || "No comment."}
                            </p>

                            <div className="review-meta-row">
                              <div className="review-rating-compact">
                                {"★".repeat(Math.floor(review.overallRating)) +
                                  "☆".repeat(
                                    5 - Math.floor(review.overallRating),
                                  )}{" "}
                                <span className="rating-number">
                                  {review.overallRating.toFixed(1)}
                                </span>
                              </div>
                              <span className="review-date-compact">
                                {new Date(
                                  review.createdAt,
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>

                  <button className="swiper-button-prev-custom">←</button>
                  <button className="swiper-button-next-custom">→</button>
                </div>

                {user ? (
                  // Logged-in: show button normally
                  <button
                    className="write-review-btn"
                    onClick={() => setShowReviewModal(true)}
                  >
                    Write a Review
                  </button>
                ) : (
                  // Guest: show a soft prompt instead of a hidden button
                  <div className="review-login-prompt">
                    <p>
                      <span className="review-lock-icon">🔒</span>
                      Want to leave a review?{" "}
                      <button
                        className="review-login-link"
                        onClick={() =>
                          navigate("/login", {
                            state: {
                              message: "Log in to write a review.",
                              redirectTo: window.location.pathname,
                            },
                          })
                        }
                      >
                        Log in
                      </button>{" "}
                      or{" "}
                      <button
                        className="review-login-link"
                        onClick={() => navigate("/decision-screen")}
                      >
                        create an account
                      </button>
                      .
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "career" && (
              <div className="tab-section career-section">
                <h3 className="section-title">Education</h3>

                <div className="education-container">
                  {hasEducation ? (
                    educationEntries.map((edu, index) => (
                      <div key={index} className="education-item">
                        <div className="school-name">{edu.schoolName}</div>
                        <div className="degree-field">
                          {edu.degree?.toUpperCase()} in {edu.fieldOfStudy}
                        </div>

                        {/* Years - you'll need to add graduationYear or startYear/endYear fields later */}
                        {edu.years && (
                          <div className="period">
                            <span className="calendar-icon">📅</span>{" "}
                            {edu.years}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="no-data">
                      Education details not provided yet.
                    </p>
                  )}

                  {/* Optional: Add Teaching Experience below if you want to keep it */}
                  {provider.teachingExperience && (
                    <>
                      <h4 className="sub-section-title mt-6">
                        Teaching Experience
                      </h4>
                      <p className="experience-text">
                        {provider.teachingExperience}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === "map" && (
              <div className="tab-section">
                <h3>Location</h3>
                <p>
                  {provider.generalLocation
                    ? `Serves the ${provider.generalLocation} area. Exact address is shared after contact is made through TutorsLinks.`
                    : "Location not available."}
                </p>
              </div>
            )}

            {activeTab === "media" && (
              <div className="tab-section media-section">
                {/* First video is already shown as the Introduction Video
                    under Overview — avoid showing it twice here */}
                {provider.videos && provider.videos.length > 1 ? (
                  <div className="pub-videos-grid">
                    {provider.videos.slice(1).map((vid) => (
                      <PublicVideoCard
                        key={vid._id}
                        vid={vid}
                        baseUrl={API_BASE_URL}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="pub-no-videos">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#cbd5e1"
                      strokeWidth="1.5"
                    >
                      <rect x="2" y="3" width="20" height="14" rx="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                    <p>
                      {provider.videos?.length === 1
                        ? "No additional videos uploaded yet."
                        : "No videos uploaded yet."}
                    </p>
                    <small>
                      {provider.videos?.length === 1
                        ? "The introduction video is shown under Overview."
                        : "Intro videos, lesson demos and virtual centre tours will appear here."}
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {showReviewModal && (
        <div className="modal-overlay">
          <div className="review-modal detailed-ratings-modal">
            <div className="modal-header">
              <h3>Write a Review</h3>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowReviewModal(false);
                  resetReviewForm();
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* <p className="review-label">Write a review</p> */}
              <p className="review-hint">Write a review on the tutor/center</p>

              <textarea
                placeholder="Write a review"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={5}
                className="review-textarea"
              />

              <div className="ratings-section">
                <h4>Rating</h4>

                <div className="rating-grid">
                  <div className="rating-item">
                    <span className="rating-label">Lesson Quality Rating</span>
                    <StarRating
                      value={ratings.lessonQuality}
                      onChange={(val) =>
                        setRatings((prev) => ({ ...prev, lessonQuality: val }))
                      }
                    />
                  </div>

                  <div className="rating-item">
                    <span className="rating-label">Performance Rating</span>
                    <StarRating
                      value={ratings.performance}
                      onChange={(val) =>
                        setRatings((prev) => ({ ...prev, performance: val }))
                      }
                    />
                  </div>

                  <div className="rating-item">
                    <span className="rating-label">Communication Rating</span>
                    <StarRating
                      value={ratings.communication}
                      onChange={(val) =>
                        setRatings((prev) => ({ ...prev, communication: val }))
                      }
                    />
                  </div>

                  <div className="rating-item">
                    <span className="rating-label">Value for Money Rating</span>
                    <StarRating
                      value={ratings.valueForMoney}
                      onChange={(val) =>
                        setRatings((prev) => ({ ...prev, valueForMoney: val }))
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowReviewModal(false);
                  resetReviewForm();
                }}
              >
                Cancel
              </button>
              <button
                className="submit-btn"
                onClick={handleSubmitReview}
                disabled={!canSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="success-modal">
            <button
              className="modal-close-btn"
              onClick={() => {
                setShowSuccessModal(false);
                setShowReviewModal(false);
              }}
              aria-label="Close"
            >
              ×
            </button>

            <div className="success-icon-wrapper">
              <div className="success-icon">✓</div>
            </div>

            <h3 className="success-title">Review Submitted</h3>

            <p className="success-message">
              Your review, comment, and rating for the tutor have been
              successfully submitted.
            </p>

            <button
              className="success-ok-btn"
              onClick={() => {
                setShowSuccessModal(false);
                setShowReviewModal(false);
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showIntroModal && (
        <div className="modal-overlay">
          <div className="success-modal">
            <button
              className="modal-close-btn"
              onClick={() => setShowIntroModal(false)}
              aria-label="Close"
            >
              ×
            </button>

            <h3 className="success-title">Request Intro Session</h3>

            <p className="success-message">
              Send {displayName} a short note about what you'd like to cover.
              They'll accept or decline, then you can arrange the details.
            </p>

            <textarea
              className="report-reason-textarea"
              placeholder="e.g. I'd like a free intro session for JAMB Physics, preferably weekday evenings..."
              rows={4}
              value={introMessage}
              onChange={(e) => setIntroMessage(e.target.value)}
            />

            <button
              className="success-ok-btn"
              onClick={handleIntroSubmit}
              disabled={introSubmitting}
            >
              {introSubmitting ? "Sending…" : "Send Request"}
            </button>
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="modal-overlay">
          <div className="success-modal">
            <button
              className="modal-close-btn"
              onClick={() => setShowReportModal(false)}
              aria-label="Close"
            >
              ×
            </button>

            <h3 className="success-title">Report {displayName}</h3>

            <p className="success-message">
              Tell us what's wrong with this profile. Our team reviews every
              report and will follow up if needed.
            </p>

            <textarea
              className="report-reason-textarea"
              placeholder="What happened? (e.g. inappropriate content, suspected scam, fake credentials, requested payment outside TutorsLinks...)"
              rows={4}
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
            />

            <button
              className="success-ok-btn"
              onClick={handleReportSubmit}
              disabled={reportSubmitting || !reportReason.trim()}
            >
              {reportSubmitting ? "Submitting…" : "Submit Report"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorProfilePage;
